import { db } from '../../config/firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { AuditService } from './AuditService';

// ─── Interfaces ───

export interface FormTemplate {
  id: string;
  name: string;
  category: 'intake' | 'treatment-plan' | 'assessment' | 'consent';
  required: boolean;
  description?: string;
}

export interface AssignedForm {
  id: string;
  clientId: string;
  providerId: string;
  formTemplateId: string;
  formTemplateName: string;
  category: string;
  required: boolean;
  assignedDate: string;
  assignedBy: string;
  completed: boolean;
  completedDate?: string;
  dueDate?: string;
  priority: 'low' | 'medium' | 'high';
}

export interface FormStatusSummary {
  total: number;
  completed: number;
  pending: number;
  overdue: number;
  canBookAppointment: boolean;
}

// ─── Static Form Templates ───

const DEFAULT_FORM_TEMPLATES: FormTemplate[] = [
  { id: 'consent-minor-software', name: 'Consent for Minor Usage of Software Services', category: 'consent', required: false, description: 'Required for clients under 18 years old' },
  { id: 'covid-screening', name: 'COVID-19 Pre-Appointment Screening Questionnaire', category: 'intake', required: false, description: 'Health screening questionnaire' },
  { id: 'release-information', name: 'Information Release Authorization', category: 'consent', required: false, description: 'Authorization to release medical information' },
  { id: 'standard-intake', name: 'Standard Intake Questionnaire Template', category: 'intake', required: true, description: 'Comprehensive intake questionnaire' },
  { id: 'financial-responsibility', name: 'Client Financial Responsibility Form', category: 'consent', required: true, description: 'Financial agreement and responsibility' },
  { id: 'behavioral-treatment-plan', name: 'Behavioral Health Treatment Plan', category: 'treatment-plan', required: false, description: 'Individual behavioral health treatment planning' },
  { id: 'couples-treatment-plan', name: 'Couples Behavioral Health Treatment Plan', category: 'treatment-plan', required: false, description: 'Couples therapy treatment planning' },
  { id: 'gad7', name: 'GAD-7 Anxiety Assessment', category: 'assessment', required: true, description: 'Generalized Anxiety Disorder 7-item scale' },
  { id: 'phq9', name: 'PHQ-9 Depression Assessment', category: 'assessment', required: true, description: 'Patient Health Questionnaire-9 for depression' },
];

// Mapping from ShareIntakeModal document IDs to form template IDs
const SHARE_DOC_TO_TEMPLATE: Record<string, string> = {
  'scored-gad7': 'gad7',
  'scored-phq9': 'phq9',
  'questionnaire-intake': 'standard-intake',
  'questionnaire-financial': 'financial-responsibility',
  'questionnaire-covid': 'covid-screening',
  'questionnaire-release': 'release-information',
  'questionnaire-minor': 'consent-minor-software',
  'consent-telehealth': 'consent-telehealth',
  'consent-ai': 'consent-ai',
  'consent-audio': 'consent-audio',
  'consent-cc': 'consent-cc',
  'consent-informed': 'consent-informed',
  'consent-privacy': 'consent-privacy',
  'consent-policies': 'consent-policies',
  'demographics-form': 'demographics-form',
  'demographics-cc': 'demographics-cc',
};

const COLLECTION_NAME = 'form_assignments';

// ─── Service ───

export class FirebaseFormAssignmentService {

  // ─── Template Lookups (synchronous, in-memory) ───

  static getAllFormTemplates(): FormTemplate[] {
    return DEFAULT_FORM_TEMPLATES;
  }

  /** Returns custom measures formatted as form templates for the assign modal */
  static customMeasuresToTemplates(measures: { id: string; title: string; description?: string }[]): FormTemplate[] {
    return measures.map(m => ({
      id: m.id,
      name: m.title,
      category: 'assessment' as const,
      required: false,
      description: m.description || 'Custom practice measure',
    }));
  }

  static getFormTemplateById(id: string): FormTemplate | undefined {
    return DEFAULT_FORM_TEMPLATES.find(t => t.id === id);
  }

  static getFormTemplatesByCategory(category: FormTemplate['category']): FormTemplate[] {
    return DEFAULT_FORM_TEMPLATES.filter(t => t.category === category);
  }

  // ─── Firestore CRUD ───

  static async getAssignmentsByClientId(clientId: string, providerId?: string): Promise<AssignedForm[]> {
    try {
      const constraints: any[] = [
        where('clientId', '==', clientId),
      ];
      // When called by a provider, include providerId filter to satisfy Firestore security rules
      if (providerId) {
        constraints.push(where('providerId', '==', providerId));
      } else {
        // Only use orderBy when no providerId (client-side), to avoid needing composite index
        constraints.push(orderBy('assignedDate', 'desc'));
      }

      const q = query(collection(db, COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);
      const results = snapshot.docs.map(d => FirebaseFormAssignmentService.docToAssignedForm(d.id, d.data()));

      // Sort in memory when providerId is present (no orderBy in query)
      if (providerId) {
        results.sort((a, b) => new Date(b.assignedDate).getTime() - new Date(a.assignedDate).getTime());
      }

      return results;
    } catch (error) {
      console.error('Error getting assignments:', error);
      return [];
    }
  }

  static async getAssignmentsByProviderAndClient(providerId: string, clientId: string): Promise<AssignedForm[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('providerId', '==', providerId),
        where('clientId', '==', clientId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => FirebaseFormAssignmentService.docToAssignedForm(d.id, d.data()));
    } catch (error) {
      console.error('Error getting assignments:', error);
      return [];
    }
  }

  static async assignFormToClient(data: {
    clientId: string;
    providerId: string;
    formTemplateId: string;
    formTemplateName?: string;
    category?: string;
    required?: boolean;
    priority?: 'low' | 'medium' | 'high';
    dueDate?: string;
  }): Promise<string> {
    // Check for existing assignment (dedup)
    const existing = await FirebaseFormAssignmentService.getAssignmentsByClientId(data.clientId, data.providerId);
    const duplicate = existing.find(a => a.formTemplateId === data.formTemplateId);
    if (duplicate) {
      return duplicate.id;
    }

    // Look up template info if not provided
    const template = FirebaseFormAssignmentService.getFormTemplateById(data.formTemplateId);
    const now = Timestamp.now();

    const docData = {
      clientId: data.clientId,
      providerId: data.providerId,
      formTemplateId: data.formTemplateId,
      formTemplateName: data.formTemplateName || template?.name || data.formTemplateId,
      category: data.category || template?.category || 'intake',
      required: data.required ?? template?.required ?? false,
      assignedDate: now,
      assignedBy: data.providerId,
      completed: false,
      completedDate: null,
      dueDate: data.dueDate ? Timestamp.fromDate(new Date(data.dueDate)) : null,
      priority: data.priority || 'medium',
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), docData);

    try {
      await AuditService.log({
        action: 'form_assigned' as any,
        userId: data.providerId,
        userRole: 'provider',
        resourceType: 'form_assignment' as any,
        resourceId: docRef.id,
        resourceOwner: data.clientId,
        details: JSON.stringify({ formTemplateId: data.formTemplateId, formName: docData.formTemplateName }),
      });
    } catch (e) {
      // Non-blocking
    }

    return docRef.id;
  }

  /**
   * Mark a measure/form as completed by templateId — finds existing assignment or creates a pre-completed one.
   * Used when measures (GAD-7, PHQ-9) are administered during session so they show in Forms tab.
   */
  static async markMeasureCompleted(data: {
    clientId: string;
    providerId: string;
    formTemplateId: string;
    formTemplateName: string;
  }): Promise<void> {
    try {
      // Check if there's an existing assignment for this form
      const existing = await FirebaseFormAssignmentService.getAssignmentsByClientId(data.clientId, data.providerId);
      const assignment = existing.find(a => a.formTemplateId === data.formTemplateId && !a.completed);

      if (assignment) {
        // Mark existing assignment as completed
        await FirebaseFormAssignmentService.markFormAsCompleted(assignment.id);
      } else {
        // No pending assignment — create a pre-completed record so it shows in Forms tab
        const template = FirebaseFormAssignmentService.getFormTemplateById(data.formTemplateId);
        const now = Timestamp.now();
        await addDoc(collection(db, COLLECTION_NAME), {
          clientId: data.clientId,
          providerId: data.providerId,
          formTemplateId: data.formTemplateId,
          formTemplateName: data.formTemplateName || template?.name || data.formTemplateId,
          category: template?.category || 'assessment',
          required: template?.required ?? false,
          assignedDate: now,
          assignedBy: data.providerId,
          completed: true,
          completedDate: now,
          dueDate: null,
          priority: 'medium',
          createdAt: now,
          updatedAt: now,
        });
      }
    } catch (err) {
      console.error('[FormAssignment] Error marking measure completed:', err);
    }
  }

  static async markFormAsCompleted(assignmentId: string): Promise<void> {
    const now = Timestamp.now();
    await updateDoc(doc(db, COLLECTION_NAME, assignmentId), {
      completed: true,
      completedDate: now,
      updatedAt: now,
    });
  }

  static async getClientFormStatus(clientId: string, providerId?: string): Promise<FormStatusSummary> {
    const assignments = await FirebaseFormAssignmentService.getAssignmentsByClientId(clientId, providerId);
    const now = new Date();
    const completed = assignments.filter(a => a.completed).length;
    const pending = assignments.filter(a => !a.completed).length;
    const overdue = assignments.filter(a => !a.completed && a.dueDate && new Date(a.dueDate) < now).length;
    const requiredIncomplete = assignments.filter(a => a.required && !a.completed);

    return {
      total: assignments.length,
      completed,
      pending,
      overdue,
      // Client can book only if they have required forms assigned AND all are completed
      canBookAppointment: assignments.length > 0 && requiredIncomplete.length === 0,
    };
  }

  static async canClientBookAppointment(clientId: string): Promise<boolean> {
    const status = await FirebaseFormAssignmentService.getClientFormStatus(clientId);
    return status.canBookAppointment;
  }

  static async removeFormAssignment(assignmentId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION_NAME, assignmentId));
  }

  static async clearAllClientAssignments(clientId: string, providerId: string): Promise<void> {
    const assignments = await FirebaseFormAssignmentService.getAssignmentsByClientId(clientId, providerId);
    for (const assignment of assignments) {
      await deleteDoc(doc(db, COLLECTION_NAME, assignment.id));
    }
  }

  // ─── Invite-Based Bulk Assignment ───

  static async assignFormsFromInvite(
    clientId: string,
    providerId: string,
    selectedDocumentIds: string[],
    completedFormIds?: string[]
  ): Promise<void> {
    const completedSet = new Set(completedFormIds || []);

    for (const shareDocId of selectedDocumentIds) {
      const templateId = SHARE_DOC_TO_TEMPLATE[shareDocId] || shareDocId;
      const template = FirebaseFormAssignmentService.getFormTemplateById(templateId);

      const now = Timestamp.now();
      const isCompleted = completedSet.has(templateId);

      const docData = {
        clientId,
        providerId,
        formTemplateId: templateId,
        formTemplateName: template?.name || shareDocId,
        category: template?.category || 'consent',
        required: template?.required ?? false,
        assignedDate: now,
        assignedBy: providerId,
        completed: isCompleted,
        completedDate: isCompleted ? now : null,
        dueDate: null,
        priority: template?.required ? 'high' : 'medium',
        createdAt: now,
        updatedAt: now,
      };

      // Dedup check
      const existing = await FirebaseFormAssignmentService.getAssignmentsByClientId(clientId, providerId);
      const duplicate = existing.find(a => a.formTemplateId === templateId);
      if (!duplicate) {
        await addDoc(collection(db, COLLECTION_NAME), docData);
      }
    }
  }

  // ─── Assign Standard Required Forms (for newly claimed clients) ───

  static async assignStandardForms(clientId: string, providerId: string): Promise<void> {
    const requiredTemplates = DEFAULT_FORM_TEMPLATES.filter(t => t.required);
    for (const template of requiredTemplates) {
      await FirebaseFormAssignmentService.assignFormToClient({
        clientId,
        providerId,
        formTemplateId: template.id,
        formTemplateName: template.name,
        category: template.category,
        required: true,
        priority: 'high',
      });
    }
  }

  // ─── Real-time Subscription ───

  static subscribeToClientAssignments(
    clientId: string,
    callback: (assignments: AssignedForm[]) => void,
    providerId?: string
  ): Unsubscribe {
    const constraints: any[] = [
      where('clientId', '==', clientId),
    ];
    if (providerId) {
      constraints.push(where('providerId', '==', providerId));
    } else {
      constraints.push(orderBy('assignedDate', 'desc'));
    }
    const q = query(collection(db, COLLECTION_NAME), ...constraints);
    return onSnapshot(q, (snapshot) => {
      const assignments = snapshot.docs.map(d =>
        FirebaseFormAssignmentService.docToAssignedForm(d.id, d.data())
      );
      // Sort in memory when providerId is present
      if (providerId) {
        assignments.sort((a, b) => new Date(b.assignedDate).getTime() - new Date(a.assignedDate).getTime());
      }
      callback(assignments);
    }, (error) => {
      console.error('Form assignments subscription error:', error);
    });
  }

  // ─── Helpers ───

  private static docToAssignedForm(id: string, data: any): AssignedForm {
    return {
      id,
      clientId: data.clientId,
      providerId: data.providerId,
      formTemplateId: data.formTemplateId,
      formTemplateName: data.formTemplateName || data.formTemplateId,
      category: data.category || 'intake',
      required: data.required ?? false,
      assignedDate: data.assignedDate?.toDate?.()?.toISOString() || new Date().toISOString(),
      assignedBy: data.assignedBy || data.providerId,
      completed: data.completed || false,
      completedDate: data.completedDate?.toDate?.()?.toISOString(),
      dueDate: data.dueDate?.toDate?.()?.toISOString(),
      priority: data.priority || 'medium',
    };
  }
}
